import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import styles from './StudentFeedbackSurvey.module.scss';

interface NpsScaleProps {
  value: number | null;
  onChange: (value: number) => void;
  lowLabel: string;
  highLabel: string;
}

/** Escala NPS 0–10 en una sola fila. */
export function NpsScale({ value, onChange, lowLabel, highLabel }: NpsScaleProps) {
  return (
    <div className={styles.nps}>
      <div className={styles.nps__grid} role="radiogroup" aria-label={`${lowLabel} – ${highLabel}`}>
        {Array.from({ length: 11 }, (_, n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={String(n)}
            className={cn(styles.nps__btn, value === n && styles['nps__btn--active'])}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        ))}
      </div>
      <div className={styles.nps__labels}>
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );
}

export interface FeedbackOption {
  value: string;
  label: string;
}

interface CardRadioGroupProps {
  options: FeedbackOption[];
  value: string | null;
  onChange: (value: string) => void;
  ariaLabel?: string;
}

/**
 * Selección única en cards (patrón del onboarding — Radix RadioGroup + card),
 * apiladas una debajo de otra.
 */
export function CardRadioGroup({ options, value, onChange, ariaLabel }: CardRadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      className={styles.cards}
      value={value ?? undefined}
      onValueChange={onChange}
      aria-label={ariaLabel}
    >
      {options.map((opt) => (
        // Toda la card es el propio control (button de Radix) → clicable entera.
        <RadioGroupPrimitive.Item
          key={opt.value}
          value={opt.value}
          className={styles.card}
          data-no-press-scale=""
        >
          <span className={styles.card__radio} aria-hidden="true">
            <RadioGroupPrimitive.Indicator className={styles.card__radioDot} />
          </span>
          <span className={styles.card__label}>{opt.label}</span>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}

interface CardCheckboxGroupProps {
  options: FeedbackOption[];
  values: string[];
  onToggle: (value: string) => void;
}

/** Selección múltiple en cards (Radix Checkbox + card), apiladas. */
export function CardCheckboxGroup({ options, values, onToggle }: CardCheckboxGroupProps) {
  return (
    <div className={styles.cards} role="group">
      {options.map((opt) => (
        // Toda la card es el checkbox (button de Radix) → clicable entera.
        <CheckboxPrimitive.Root
          key={opt.value}
          checked={values.includes(opt.value)}
          onCheckedChange={() => onToggle(opt.value)}
          className={styles.card}
          data-no-press-scale=""
        >
          <span className={styles.card__check} aria-hidden="true">
            <CheckboxPrimitive.Indicator className={styles.card__checkIndicator}>
              <Check size={14} strokeWidth={3} aria-hidden />
            </CheckboxPrimitive.Indicator>
          </span>
          <span className={styles.card__label}>{opt.label}</span>
        </CheckboxPrimitive.Root>
      ))}
    </div>
  );
}

interface FeedbackTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

/** Campo de texto libre para las preguntas abiertas. */
export function FeedbackTextarea({ value, onChange, placeholder }: FeedbackTextareaProps) {
  return (
    <textarea
      className={styles.textarea}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={5}
      maxLength={2000}
    />
  );
}
